import { Link, useNavigate } from "react-router-dom";
import { buildLoginUrl } from "@/lib/redirect";
import { Button } from "@/components/ui/button";
import { Search, User } from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import { GlobalSearchOverlay } from "@/components/search/GlobalSearchOverlay";
import { useAuth } from "@/context/AuthContext";
import { useAccountMenuProps } from "@/hooks/useAccountMenuProps";
import { AccountMenu } from "@/components/nav/AccountMenu";
import { LocaleSwitcher } from "@/components/common/LocaleSwitcher";
import { useTranslate } from "@/i18n";

interface LandingHeaderProps {
  /** "lens" = editorial silence, "app" = full navigation */
  variant?: "lens" | "app";
}

const LandingHeader = ({ variant = "app" }: LandingHeaderProps) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const accountProps = useAccountMenuProps();
  const t = useTranslate();

  const [searchOpen, setSearchOpen] = useState(false);
  const closeSearch = useCallback(() => setSearchOpen(false), []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen((prev) => !prev);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const isLens = variant === "lens";

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-colors ${
        isLens
          ? "bg-background"
          : "bg-background/80 backdrop-blur-md border-b border-border/40"
      }`}
    >
      <div
        className={`container flex items-center justify-between ${
          isLens ? "h-16 sm:h-20" : "h-14"
        }`}
      >
        <Link to="/" className="flex items-center">
          <span
            className={`font-display font-medium text-foreground ${
              isLens
                ? "text-lg tracking-wide text-foreground/80"
                : "text-xl"
            }`}
          >
            Farmly
          </span>
        </Link>

        <nav className="flex items-center gap-4 sm:gap-6">
          {/* Search trigger */}
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 rounded-full text-muted-foreground hover:text-foreground"
            onClick={() => setSearchOpen(true)}
            title={`${t("nav.search")} (⌘K)`}
          >
            <Search className="h-4 w-4" />
            <span className="sr-only">{t("nav.search")}</span>
          </Button>

          {/* Locale switcher (public surfaces only) */}
          <LocaleSwitcher density={isLens ? "compact" : "default"} />

          {/* Account */}
          {user ? (
            <AccountMenu
              {...accountProps}
              trigger={
                <Button
                  variant="ghost"
                  size="icon"
                  className="relative h-9 w-9 rounded-full text-muted-foreground hover:text-foreground"
                  aria-label={t("nav.account")}
                >
                  <User className="h-4 w-4" />
                  <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-[#B9B0A6]" />
                </Button>
              }
            />
          ) : (
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 rounded-full text-muted-foreground hover:text-foreground"
              onClick={() => navigate(buildLoginUrl())}
              aria-label={t("nav.signIn")}
            >
              <User className="h-4 w-4" />
            </Button>
          )}
        </nav>
      </div>
      <GlobalSearchOverlay open={searchOpen} onClose={closeSearch} />
    </header>
  );
};

export default LandingHeader;
